import { useEffect, useRef, useState } from 'react';

/**
 * Fades and lifts a child into place the first time it scrolls into view.
 *
 * Deliberately restrained: 14px of travel and 380ms. A feed is something people
 * scan quickly, so the motion should register as polish rather than as something
 * they have to wait for. It fires once per element and never on the way back up,
 * which is what stops a long scroll feeling busy.
 *
 * Two things it will not do:
 *  - animate anything already on screen at mount (the first screenful appears
 *    instantly, so the feed never looks like it is loading twice)
 *  - animate at all when the user has asked for reduced motion
 */
export default function RevealOnScroll({ children, index = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [instant, setInstant] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const el = ref.current;
    if (reduced || !el || typeof IntersectionObserver === 'undefined') {
      setInstant(true);
      setShown(true);
      return undefined;
    }

    // Already visible at mount - show it with no animation at all.
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight) {
      setInstant(true);
      setShown(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(14px)',
        transition: instant
          ? 'none'
          : `opacity 380ms cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(index, 4) * 40}ms,
             transform 380ms cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(index, 4) * 40}ms`,
        willChange: shown ? 'auto' : 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}
